/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useTranslation } from 'react-i18next';
import { Typography as Text } from '@mui/material';

import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';

import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import { track } from '../../utils/track';

export default function Plans({ showEnterprise = false }) {
  const { t } = useTranslation();
  const freemiumFeatures = [
    {
      title: t('Obegränsade transkriberingar'),
      included: true,
    },
    {
      title: t('10 AI-svar per månad'),
      included: true,
    },
    {
      title: t('En export per månad'),
      included: true,
    },
    {
      title: t('Snabbare leveranser av transkriberingar'),
      included: false,
    },
    {
      title: t('Översättning av transkriberingar'),
      included: false,
    },
    {
      title: t('Fler exportalternativ (Word, Excel, SRT, VTT)'),
      included: false,
    },
  ];

  const proFeatures = [
    {
      title: t('Obegränsade transkriberingar'),
      included: true,
    },
    {
      title: t('Obegränsade AI-svar'),
      included: true,
    },
    {
      title: t('Obegränsat antal exporter'),
      included: true,
    },
    {
      title: t('Snabbare leveranser av transkriberingar'),
      included: true,
    },
    {
      title: t('Översättning av transkriberingar'),
      included: true,
    },
    {
      title: t('Fler exportalternativ (Word, Excel, SRT, VTT)'),
      included: true,
    },
  ];
  const enterpriseFeatures = [
    {
      title: t('Transkribera obegränsat'),
      included: true,
    },
    {
      title: t('Prioriterad leverans av transkriberingar'),
      included: true,
    },
    {
      title: t('Obegränsat antal AI-svar'),
      included: true,
    },
    {
      title: t('Fler exporter per månad'),
      included: true,
    },
    {
      title: t('Avancerade AI-block och mallar'),
      included: true,
    },
    {
      title: t('Obegränsad talaridentifiering'),
      included: true,
    },
    {
      title: t('Kundanpassade mallar'),
      included: true,
    },
    {
      title: t('Sätt eget namn och bild på AI-sekreteraren'),
      included: true,
    },
    {
      title: t('Transkribering av telefonsamtal'),
      included: true,
    },
    {
      title: t('Inloggning med BankID'),
      included: true,
    },
    {
      title: t('Integrationer med andra system'),
      included: true,
    },
    {
      title: t('Fakturering'),
      included: true,
    },
    {
      title: t('SLA, DPA, API'),
      included: true,
    },
  ];

  return (
    <div
      css={css`
        margin-top: 16px;
        margin-bottom: 24px;
        gap: 8px;
        display: flex;
        flex-wrap: wrap;
      `}
    >
      <div
        css={css`
          flex-basis: calc(50% - 4px);
          flex-grow: 1;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;

          @media (max-width: 700px) {
            flex-basis: 100%;
          }
        `}
      >
        <div
          css={css`
            padding: 8px 16px;
            border-radius: 16px;
            background: rgba(32, 40, 250, 0.08);
            box-sizing: border-box;
            min-width: 200px;
            flex-grow: 1;
          `}
        >
          <Text
            variant="h6"
            paragraph
            color="primary"
            css={css`
              font-weight: 600;
            `}
          >
            {t('Professionell')}
          </Text>
          <div>
            {proFeatures.map((feature, index) => (
              <Text
                key={index}
                variant="body2"
                component="div"
                css={css`
                  padding: 8px 0;
                  border-bottom: 1px solid rgba(0, 0, 0, 0.23);
                  &:last-child {
                    border-bottom: none;
                  }
                  display: flex;
                  font-weight: 600;
                  color: #161caf;
                  align-items: center;
                `}
              >
                {feature.title}
                <div
                  css={css`
                    margin-left: auto;
                  `}
                >
                  {feature.included ? (
                    <CheckIcon
                      sx={{ verticalAlign: 'bottom', marginRight: '8px' }}
                      color="primary"
                    />
                  ) : (
                    <ClearIcon
                      sx={{ verticalAlign: 'bottom', marginRight: '8px' }}
                      color="error"
                    />
                  )}
                </div>
              </Text>
            ))}
          </div>
        </div>
        <Button
          component={Link}
          onClick={() => {
            track('onboarding_upgrade_button');
          }}
          to="/profile/billing"
          variant="contained"
          color="secondary"
          fullWidth
        >
          {t('Prova gratis')}
        </Button>
      </div>
      <div
        css={css`
          padding: 8px 16px;
          border-radius: 16px;
          border: 1px solid rgba(0, 0, 0, 0.23);
          flex-basis: calc(50% - 4px);
          box-sizing: border-box;
          min-width: 200px;
          flex-grow: 1;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        `}
      >
        <Text
          css={css`
            font-weight: 600;
          `}
          variant="h6"
          paragraph
        >
          {showEnterprise ? t('Enterprise') : t('Gratis')}
        </Text>
        <div>
          {(showEnterprise ? enterpriseFeatures : freemiumFeatures).map(
            (feature, index) => (
              <Text
                key={index}
                variant="body2"
                component="div"
                css={css`
                  padding: 8px 0;
                  border-bottom: 1px solid rgba(0, 0, 0, 0.23);
                  &:last-child {
                    border-bottom: none;
                  }
                  display: flex;
                  align-items: center;
                `}
              >
                {feature.title}
                <div
                  css={css`
                    margin-left: auto;
                  `}
                >
                  {feature.included ? (
                    <CheckIcon
                      sx={{ verticalAlign: 'bottom', marginRight: '8px' }}
                      color="success"
                    />
                  ) : (
                    <ClearIcon
                      sx={{ verticalAlign: 'bottom', marginRight: '8px' }}
                      color="error"
                    />
                  )}
                </div>
              </Text>
            )
          )}
        </div>
        {showEnterprise && (
          <Button
            component="a"
            href="https://klang.ai/support/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              track('enterprise_contact_button');
            }}
            variant="outlined"
            color="primary"
            fullWidth
          >
            {t('Kontakta oss')}
          </Button>
        )}
      </div>
    </div>
  );
}
